import * as React from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import {
  DEFAULT_OG_IMAGE_PATH,
  LANDING_DESCRIPTION,
  LANDING_TITLE,
  SITE_URL,
  landingStructuredData,
} from '../../features/marketing/landing-seo';

type RouteMetadata = {
  canonicalPath?: string;
  description: string;
  robots: string;
  structuredData?: Record<string, unknown>;
  title: string;
};

const DEFAULT_THEME_COLOR = '#16323b';
const DEFAULT_OG_IMAGE_URL = `${SITE_URL}${DEFAULT_OG_IMAGE_PATH}`;

const routeMetadata: Array<{
  metadata: RouteMetadata;
  pattern: string;
}> = [
  {
    pattern: '/',
    metadata: {
      title: LANDING_TITLE,
      description: LANDING_DESCRIPTION,
      robots: 'index,follow',
      canonicalPath: '/',
      structuredData: landingStructuredData,
    },
  },
  {
    pattern: '/dpa',
    metadata: {
      title: 'Acordo de Tratamento de Dados | BelaHora',
      description:
        'Modelo público do Acordo de Tratamento de Dados da BelaHora para clientes e contratos de serviço.',
      robots: 'index,follow',
      canonicalPath: '/dpa',
    },
  },
  {
    pattern: '/privacidade',
    metadata: {
      title: 'Política de Privacidade | BelaHora',
      description:
        'Como a BelaHora recolhe e trata dados pessoais no site e na aplicação, incluindo finalidades, fornecedores e direitos do utilizador.',
      robots: 'index,follow',
      canonicalPath: '/privacidade',
    },
  },
  {
    pattern: '/cookies',
    metadata: {
      title: 'Política de Cookies | BelaHora',
      description:
        'Rastreadores utilizados pela BelaHora, todos estritamente necessários ao funcionamento do serviço, e como controlá-los no navegador.',
      robots: 'index,follow',
      canonicalPath: '/cookies',
    },
  },
  {
    pattern: '/termos',
    metadata: {
      title: 'Termos e Condições | BelaHora',
      description:
        'Condições de utilização da BelaHora por profissionais e empresas, incluindo conta, subscrição, responsabilidade e proteção de dados.',
      robots: 'index,follow',
      canonicalPath: '/termos',
    },
  },
  {
    pattern: '/b/:slug/privacy',
    metadata: {
      title: 'Aviso de Privacidade | BelaHora',
      description:
        'Informação pública sobre o tratamento de dados nas marcações online operadas pela BelaHora.',
      robots: 'noindex,follow',
    },
  },
  {
    pattern: '/b/:slug',
    metadata: {
      title: 'Marcação Online | BelaHora',
      description:
        'Página pública de marcação online operada pela BelaHora para negócios locais.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/cancel/:token',
    metadata: {
      title: 'Cancelar Marcação | BelaHora',
      description:
        'Gestão pública de cancelamentos de marcações operadas pela BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/cancel-appointment',
    metadata: {
      title: 'Cancelar Marcação | BelaHora',
      description:
        'Gestão pública de cancelamentos de marcações operadas pela BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/login',
    metadata: {
      title: 'Entrar | BelaHora',
      description: 'Acesso reservado à equipa e aos clientes BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/register',
    metadata: {
      title: 'Como Obter Acesso | BelaHora',
      description: 'Fluxo privado de criação de conta para acesso ao BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/recover-password',
    metadata: {
      title: 'Recuperar Password | BelaHora',
      description: 'Recuperação de acesso à conta BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/forgot-password',
    metadata: {
      title: 'Redefinir Password | BelaHora',
      description: 'Redefinição de password da conta BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/reset-password',
    metadata: {
      title: 'Redefinir Password | BelaHora',
      description: 'Redefinição de password da conta BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/dashboard',
    metadata: {
      title: 'Dashboard | BelaHora',
      description: 'Área privada da operação BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/analytics',
    metadata: {
      title: 'Análises | BelaHora',
      description: 'Área privada de análises BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/clients',
    metadata: {
      title: 'Clientes | BelaHora',
      description: 'Área privada de clientes BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/clients/:clientId',
    metadata: {
      title: 'Cliente | BelaHora',
      description: 'Área privada de detalhe de cliente BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/settings/personal',
    metadata: {
      title: 'Definições Pessoais | BelaHora',
      description: 'Área privada de definições pessoais BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/settings/business',
    metadata: {
      title: 'Definições do Negócio | BelaHora',
      description: 'Área privada de definições do negócio BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/settings/staff',
    metadata: {
      title: 'Equipa | BelaHora',
      description: 'Área privada de gestão de equipa BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/settings/services',
    metadata: {
      title: 'Serviços | BelaHora',
      description: 'Área privada de gestão de serviços BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/settings/availability',
    metadata: {
      title: 'Disponibilidade | BelaHora',
      description: 'Área privada de gestão de disponibilidade BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
  {
    pattern: '/onboarding',
    metadata: {
      title: 'Onboarding | BelaHora',
      description: 'Área privada de onboarding BelaHora.',
      robots: 'noindex,nofollow',
    },
  },
];

function getRouteMetadata(pathname: string): RouteMetadata {
  const match = routeMetadata.find(({ pattern }) =>
    matchPath({ path: pattern, end: true }, pathname)
  );

  if (match) {
    return match.metadata;
  }

  return {
    title: LANDING_TITLE,
    description: LANDING_DESCRIPTION,
    robots: 'noindex,nofollow',
  };
}

function upsertMeta(
  selector: string,
  attributes: Record<string, string>,
  content: string
) {
  const existingElement = document.head.querySelector<HTMLMetaElement>(selector);
  const element = existingElement ?? document.createElement('meta');

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  element.setAttribute('content', content);
  element.setAttribute('data-route-metadata', 'true');

  if (!existingElement) {
    document.head.appendChild(element);
  }
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  const existingElement = document.head.querySelector<HTMLLinkElement>(selector);
  const element = existingElement ?? document.createElement('link');

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  element.setAttribute('data-route-metadata', 'true');

  if (!existingElement) {
    document.head.appendChild(element);
  }
}

function syncStructuredData(structuredData?: Record<string, unknown>) {
  document.head
    .querySelectorAll('[data-route-metadata-jsonld="true"]')
    .forEach((node) => node.remove());

  if (!structuredData) {
    return;
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-route-metadata-jsonld', 'true');
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

export function RouteMetadataManager() {
  const location = useLocation();

  React.useLayoutEffect(() => {
    const metadata = getRouteMetadata(location.pathname);
    const canonicalPath = metadata.canonicalPath ?? location.pathname;
    const absoluteUrl = new URL(canonicalPath, SITE_URL).toString();

    document.documentElement.lang = 'pt-PT';
    document.title = metadata.title;

    upsertMeta('meta[name="description"]', { name: 'description' }, metadata.description);
    upsertMeta('meta[name="robots"]', { name: 'robots' }, metadata.robots);
    upsertMeta('meta[name="theme-color"]', { name: 'theme-color' }, DEFAULT_THEME_COLOR);
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'website');
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, metadata.title);
    upsertMeta(
      'meta[property="og:description"]',
      { property: 'og:description' },
      metadata.description
    );
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, absoluteUrl);
    upsertMeta(
      'meta[property="og:image"]',
      { property: 'og:image' },
      DEFAULT_OG_IMAGE_URL
    );
    upsertMeta(
      'meta[property="og:image:alt"]',
      { property: 'og:image:alt' },
      'Identidade visual BelaHora'
    );
    upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'pt_PT');
    upsertMeta(
      'meta[property="og:site_name"]',
      { property: 'og:site_name' },
      'BelaHora'
    );
    upsertMeta(
      'meta[name="twitter:card"]',
      { name: 'twitter:card' },
      'summary_large_image'
    );
    upsertMeta(
      'meta[name="twitter:title"]',
      { name: 'twitter:title' },
      metadata.title
    );
    upsertMeta(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      metadata.description
    );
    upsertMeta(
      'meta[name="twitter:image"]',
      { name: 'twitter:image' },
      DEFAULT_OG_IMAGE_URL
    );
    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: absoluteUrl });

    syncStructuredData(metadata.structuredData);
  }, [location.pathname]);

  return null;
}
